import {
  IMusimapCollectionGenericItem,
  IMusimapFilterCore,
  IMusimapTag,
} from '../musimap-common/musimap-common.interface';
import { BehaviorSubject, combineLatest, Observable } from 'rxjs';
import {
  getZippedCollections,
  getZippedSelection,
  MusimapCollection,
  MusimapCollectionMethods,
  MusimapCollectionProps,
} from './musimap-collection.model';
import {
  MUSIMAP_DIVISIONS,
  MUSIMAP_OPERATORS,
  MUSIMAP_SELECTION_STATES,
  MUSIMAP_WIDGETS,
} from '../musimap-common/musimap-common.enum';
import {
  asArray,
  compareObjects,
  getSourceValues,
  getTagsFilterStatus,
  isArray,
  isFunction,
  isNull,
} from '../musimap-common/musimap-common.utils';
import { distinctUntilChanged, map } from 'rxjs/operators';
import { SearchTermType } from '../../../shared/enums/typeahead';

const DEFAULT_INDEX_PROPERTY = 'label';

const { NOT_SELECTED, INCLUDED_ALL, EXCLUDED_ALL, EXCLUDED_SOME, INCLUDED_SOME, MIXED } = MUSIMAP_SELECTION_STATES;

export class MusimapFilterModel {
  private readonly default: any;

  private readonly proto: IMusimapFilterCore;

  public readonly wasUpdated: BehaviorSubject<number> = new BehaviorSubject<number>(0);

  public readonly isActive: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

  public readonly isEnabled: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

  public isConjunctive: BehaviorSubject<boolean>;

  public readonly items: MusimapCollection<IMusimapCollectionGenericItem>;

  public readonly range: BehaviorSubject<[number, number]>;

  public readonly state: BehaviorSubject<string>;

  public readonly count: BehaviorSubject<number>;

  public readonly preferences: Set<any>;

  public readonly ignoredPreferences: Set<any>;

  public openedLocation: any;

  constructor(proto: IMusimapFilterCore, defaultValue: any = null) {
    const { min, max, values, type, operator, sources } = proto;
    const indexBy = DEFAULT_INDEX_PROPERTY;

    const createCollection = (
      mapHandler: any,
      allowMultiselect: boolean = false
    ): MusimapCollection<IMusimapCollectionGenericItem> =>
      new MusimapCollection<IMusimapCollectionGenericItem>({
        indexBy,
        allowMultiselect,
      }).add((isArray(values) ? values : Object.keys(values)).map(mapHandler));

    this.proto = proto;
    this.default = defaultValue;

    if (operator) {
      this.isConjunctive = new BehaviorSubject<boolean>(operator === MUSIMAP_OPERATORS.AND);
    }

    switch (type) {
      case MUSIMAP_WIDGETS.RANGE_SELECTOR:
      case MUSIMAP_WIDGETS.CHART_RANGE_SELECTOR:
        this.range = new BehaviorSubject<[number, number]>([min, max]);
        break;

      case MUSIMAP_WIDGETS.OPTIONS_SELECTOR:
        this.items = createCollection(val => ({
          label: `${val}`,
          value: +val,
        }));
        break;

      case MUSIMAP_WIDGETS.DIVISIONS_SELECTOR:
        this.items = createCollection(
          val => ({
            label: `${val}`,
            parent: this.label,
            category: [MUSIMAP_DIVISIONS.WCM, MUSIMAP_DIVISIONS.WCM_MLR, MUSIMAP_DIVISIONS.NON_WCM].some(
              str => str === val
            )
              ? MUSIMAP_DIVISIONS.WCM
              : MUSIMAP_DIVISIONS.WMG,
          }),
          true
        );
        break;

      default:
        this.items = createCollection(
          val => ({
            label: `${val}`,
            parent: this.label,
            value: MUSIMAP_SELECTION_STATES.NOT_SELECTED,
            preference: MUSIMAP_SELECTION_STATES.NOT_SELECTED,
          }),
          type !== MUSIMAP_WIDGETS.MULTI_LEVEL_TAGS_SELECTOR
        );
        this.preferences = new Set<any>();
        this.ignoredPreferences = new Set<any>();

        if (type === MUSIMAP_WIDGETS.MULTI_SOURCE_TAGS_SELECTOR) {
          this.items.getItems().forEach((item, i) => {
            const nestedItems = asArray(getSourceValues(sources[i])).map(
              val =>
                ({
                  label: val,
                  parent: this.label,
                  category: item.label,
                  value: MUSIMAP_SELECTION_STATES.NOT_SELECTED,
                  preference: MUSIMAP_SELECTION_STATES.NOT_SELECTED,
                } as IMusimapCollectionGenericItem)
            );
            const childCollectionBuilder = item[MusimapCollectionMethods.CREATE_CHILD_COLLECTION];

            if (isFunction(childCollectionBuilder)) {
              childCollectionBuilder<IMusimapCollectionGenericItem>({
                indexBy: DEFAULT_INDEX_PROPERTY,
                allowMultiselect: true,
                parentItem: item,
              }).add(nestedItems);
            }
          });
        } else if (type === MUSIMAP_WIDGETS.MULTI_LEVEL_TAGS_SELECTOR) {
          const childCollections = getSourceValues(sources[0]);
          this.items.getItems().forEach(item => {
            const nestedItems = asArray(childCollections[item.label]).map(val => {
              item.category = item.category || '';
              item.category += `#${val}`;
              return {
                label: val,
                parent: this.label,
                category: item.label,
                value: MUSIMAP_SELECTION_STATES.NOT_SELECTED,
                preference: MUSIMAP_SELECTION_STATES.NOT_SELECTED,
              } as IMusimapCollectionGenericItem;
            });

            const childCollectionBuilder = item[MusimapCollectionMethods.CREATE_CHILD_COLLECTION];

            if (isFunction(childCollectionBuilder)) {
              childCollectionBuilder<IMusimapCollectionGenericItem>({
                indexBy: DEFAULT_INDEX_PROPERTY,
                allowMultiselect: true,
                parentItem: item,
              }).add(nestedItems);
            }
          });
        }

        this.count = new BehaviorSubject<number>(0);
        this.state = new BehaviorSubject<string>(MUSIMAP_SELECTION_STATES.NOT_SELECTED);
        break;
    }
  }

  public setOperator = (operator: MUSIMAP_OPERATORS): this => {
    this.proto.operator = operator;
    this.isConjunctive
      ? this.isConjunctive.next(operator === MUSIMAP_OPERATORS.AND)
      : (this.isConjunctive = new BehaviorSubject(operator === MUSIMAP_OPERATORS.AND));
    this.update();
    return this;
  };

  public reset = (): this => {
    switch (this.type) {
      case MUSIMAP_WIDGETS.RANGE_SELECTOR:
      case MUSIMAP_WIDGETS.CHART_RANGE_SELECTOR:
        this.range.next(this.defaultValue);
        break;

      case MUSIMAP_WIDGETS.DIVISIONS_SELECTOR:
      case MUSIMAP_WIDGETS.OPTIONS_SELECTOR:
        this.items.deselectAll().select(this.defaultValue);
        break;

      case MUSIMAP_WIDGETS.SIMPLE_TAGS_SELECTOR:
      case MUSIMAP_WIDGETS.CHART_SELECTOR:
      case MUSIMAP_WIDGETS.TEXT_SELECTOR:
      case MUSIMAP_WIDGETS.SIMPLE_TEXT_SELECTOR:
        this.items.selection.forEach(item => (item.value = MUSIMAP_SELECTION_STATES.NOT_SELECTED));
        this.items.deselectAll();
        break;

      case MUSIMAP_WIDGETS.MULTI_SOURCE_TAGS_SELECTOR:
      case MUSIMAP_WIDGETS.MULTI_LEVEL_TAGS_SELECTOR:
        getZippedSelection(this.items).forEach(item => {
          item.value = NOT_SELECTED;
          item[MusimapCollectionMethods.TOGGLE](false);
        });
        break;
    }

    this.update(false);
    return this;
  };

  public update = (isActive: boolean | null = null): this => {
    if (!isNull(isActive)) {
      this.isActive.next(isActive);
    }
    this.wasUpdated.next(this.wasUpdated.value + 1);
    return this;
  };

  public applyOperatorChange = (collection: MusimapCollection<IMusimapCollectionGenericItem> | null = null): this => {
    if (this.isConjunctive?.value) {
      const targetCollection = collection || this.items;
      const { excludedItems } = this.getItemsDividedByCategories(targetCollection.getItems());
      targetCollection
        .deselect(excludedItems)
        .getItems(excludedItems)
        .forEach(item => {
          item.value = NOT_SELECTED;
          if (item.preference !== NOT_SELECTED) {
            const preferenceId =
              this.type === MUSIMAP_WIDGETS.SIMPLE_TAGS_SELECTOR ||
              MUSIMAP_WIDGETS.CHART_SELECTOR ||
              MUSIMAP_WIDGETS.TEXT_SELECTOR ||
              MUSIMAP_WIDGETS.SIMPLE_TEXT_SELECTOR
                ? item.label
                : `${item.category}~${item.label}`;

            this.preferences.delete(preferenceId);
            item.preference = NOT_SELECTED;
          }
        });
    }

    return this;
  };

  public asObservable = (): Observable<this> => {
    const { type } = this;
    const observables: any[] = [this.isActive.asObservable()];

    if (this.isConjunctive) {
      observables.push(this.isConjunctive.asObservable());
    }

    switch (type) {
      case MUSIMAP_WIDGETS.MULTI_SOURCE_TAGS_SELECTOR:
      case MUSIMAP_WIDGETS.MULTI_LEVEL_TAGS_SELECTOR:
      case MUSIMAP_WIDGETS.SIMPLE_TAGS_SELECTOR:
      case MUSIMAP_WIDGETS.CHART_SELECTOR:
      case MUSIMAP_WIDGETS.TEXT_SELECTOR:
      case MUSIMAP_WIDGETS.SIMPLE_TEXT_SELECTOR:
        observables.push(this.wasUpdated.asObservable());
        break;

      case MUSIMAP_WIDGETS.OPTIONS_SELECTOR:
      case MUSIMAP_WIDGETS.DIVISIONS_SELECTOR:
        observables.push(this.items.onSelect());
        break;

      case MUSIMAP_WIDGETS.RANGE_SELECTOR:
      case MUSIMAP_WIDGETS.CHART_RANGE_SELECTOR:
        observables.push(this.range.asObservable());
        break;
    }

    return combineLatest(observables).pipe(
      distinctUntilChanged(compareObjects),
      map(() => this)
    );
  };

  public toggleState = (
    collection: MusimapCollection<IMusimapCollectionGenericItem>,
    items: IMusimapCollectionGenericItem | IMusimapCollectionGenericItem[] | any | null = null,
    searchType: SearchTermType = undefined,
    state: string | null = null,
    hideExcludeState: boolean = false
  ): this => {
    if (collection && this.items) {
      const collectionSlice = collection.getItems(items);
      const { includedItems, excludedItems } = this.getItemsDividedByCategories(collectionSlice);
      const currentState = getTagsFilterStatus(collectionSlice.length, includedItems.length, excludedItems.length);
      let nextState: string = state || null;

      if (isNull(state)) {
        if (searchType === SearchTermType.Keyword || !searchType) {
          switch (currentState) {
            case NOT_SELECTED:
            case INCLUDED_SOME:
            case MIXED:
              nextState = INCLUDED_ALL;
              break;
            case INCLUDED_ALL:
            case EXCLUDED_SOME:
              nextState = this.isConjunctive?.value || hideExcludeState ? NOT_SELECTED : EXCLUDED_ALL;
              break;
            case EXCLUDED_ALL:
              nextState = NOT_SELECTED;
              break;
            default:
              nextState = NOT_SELECTED;
              break;
          }
        } else {
          switch (currentState) {
            case NOT_SELECTED:
            case INCLUDED_SOME:
            case MIXED:
              nextState = INCLUDED_ALL;
              break;
            case INCLUDED_ALL:
              nextState = NOT_SELECTED;
              break;
            default:
              nextState = NOT_SELECTED;
              break;
          }
        }
      }

      collection.getItems(items).forEach(item => {
        item.value = nextState;
        if (nextState === NOT_SELECTED) {
          item[MusimapCollectionMethods.TOGGLE](item.preference !== MUSIMAP_SELECTION_STATES.NOT_SELECTED);
        } else {
          item[MusimapCollectionMethods.TOGGLE](true);
        }
      });
    }

    return this;
  };

  public getItemsDividedByCategories = (
    items: IMusimapCollectionGenericItem[] | null = null
  ): {
    includedItems: IMusimapCollectionGenericItem[];
    excludedItems: IMusimapCollectionGenericItem[];
  } => {
    return (isNull(items) ? this.items.getItems() : asArray(items)).reduce(
      (agg, item) => {
        const { value, preference } = item;
        const useValue = value !== NOT_SELECTED || item[MusimapCollectionProps.IS_SELECTED] ? value : preference;

        if (useValue === INCLUDED_ALL) {
          agg.includedItems.push(item);
        } else if (useValue === EXCLUDED_ALL) {
          agg.excludedItems.push(item);
        }

        return agg;
      },
      {
        includedItems: [],
        excludedItems: [],
      }
    );
  };

  public getItems = (
    getPreferences: boolean = false,
    skipIgnoredPreferences: boolean = false
  ): Map<any, IMusimapCollectionGenericItem[]> => {
    const itemsMap: Map<any, IMusimapCollectionGenericItem[]> = new Map<any, IMusimapCollectionGenericItem[]>();
    const preferencesList: string[] = skipIgnoredPreferences
      ? Array.from(this.preferences).filter(p => !this.ignoredPreferences.has(p))
      : Array.from(this.preferences);

    switch (this.type) {
      case MUSIMAP_WIDGETS.SIMPLE_TAGS_SELECTOR:
      case MUSIMAP_WIDGETS.TEXT_SELECTOR:
      case MUSIMAP_WIDGETS.SIMPLE_TEXT_SELECTOR:
      case MUSIMAP_WIDGETS.CHART_SELECTOR:
        const items = getPreferences ? this.items.getItems(preferencesList) : this.items.selection;
        if (items.length) {
          itemsMap.set(this.label, items);
        }
        break;

      case MUSIMAP_WIDGETS.MULTI_SOURCE_TAGS_SELECTOR:
      case MUSIMAP_WIDGETS.MULTI_LEVEL_TAGS_SELECTOR:
        if (getPreferences) {
          const preferences = preferencesList.reduce((agg, p) => {
            const [category, item] = p.split('~');
            agg[category] = agg[category] || [];
            agg[category].push(item);
            return agg;
          }, {});
          Object.keys(preferences).forEach(key => {
            const childCollection = (this.items.getItem(key) || {})[MusimapCollectionProps.CHILD_COLLECTION];
            if (childCollection) {
              itemsMap.set(key, childCollection.getItems(preferences[key]));
            }
          });
        } else {
          this.items.getItems().forEach(item => {
            const childCollection: MusimapCollection<IMusimapCollectionGenericItem> =
              item[MusimapCollectionProps.CHILD_COLLECTION];
            if (childCollection?.hasSelection) {
              itemsMap.set(item.label, childCollection.selection);
            }
          });
        }
    }

    return itemsMap;
  };

  public setItems(item: any): void {
    this.items.add(item);
  }

  public savePreferences = (): this => {
    const { type } = this;

    const addToPreferences = (
      collection: MusimapCollection<IMusimapCollectionGenericItem>,
      prefix: string = ''
    ): void => {
      const { selection } = collection;

      selection.forEach(item => {
        const preferenceId = `${prefix}${item.label}`;
        if (item.value === NOT_SELECTED) {
          this.preferences.delete(preferenceId);
        } else {
          this.preferences.add(preferenceId);
        }
        item.preference = item.value;
        item.value = NOT_SELECTED;
      });

      collection.deselectAll();
    };

    switch (type) {
      case MUSIMAP_WIDGETS.SIMPLE_TAGS_SELECTOR:
      case MUSIMAP_WIDGETS.TEXT_SELECTOR:
      case MUSIMAP_WIDGETS.SIMPLE_TEXT_SELECTOR:
      case MUSIMAP_WIDGETS.CHART_SELECTOR:
        addToPreferences(this.items);
        break;

      case MUSIMAP_WIDGETS.MULTI_LEVEL_TAGS_SELECTOR:
      case MUSIMAP_WIDGETS.MULTI_SOURCE_TAGS_SELECTOR:
        getZippedCollections(this.items).forEach(collection =>
          addToPreferences(collection, `${collection.parentItem?.label}~`)
        );
        break;
    }

    return this;
  };

  public clearSelection = (category: string | null = null): this => {
    const { type } = this;

    const deselectAll = (collection: MusimapCollection<IMusimapCollectionGenericItem>): void => {
      collection.selection.forEach(item => (item.value = NOT_SELECTED));
      collection.deselectAll();
    };

    switch (type) {
      case MUSIMAP_WIDGETS.SIMPLE_TAGS_SELECTOR:
      case MUSIMAP_WIDGETS.TEXT_SELECTOR:
      case MUSIMAP_WIDGETS.SIMPLE_TEXT_SELECTOR:
      case MUSIMAP_WIDGETS.CHART_SELECTOR:
        deselectAll(this.items);
        break;

      case MUSIMAP_WIDGETS.MULTI_LEVEL_TAGS_SELECTOR:
      case MUSIMAP_WIDGETS.MULTI_SOURCE_TAGS_SELECTOR:
        const childCollection = (this.items.getItem(category) || {})[MusimapCollectionProps.CHILD_COLLECTION];
        if (childCollection) {
          deselectAll(childCollection);
        }
        break;
    }

    return this;
  };

  public clearPreferences = (category: string | null = null): this => {
    const prefix =
      isNull(category) ||
      this.type === MUSIMAP_WIDGETS.SIMPLE_TAGS_SELECTOR ||
      this.type === MUSIMAP_WIDGETS.TEXT_SELECTOR ||
      this.type === MUSIMAP_WIDGETS.SIMPLE_TEXT_SELECTOR ||
      this.type === MUSIMAP_WIDGETS.CHART_SELECTOR
        ? ''
        : `${category}~`;
    this.getItems(true)
      .get(prefix ? category : this.label)
      .forEach(item => {
        item.preference = NOT_SELECTED;
        this.preferences.delete(`${prefix}${item.label}`);
      });
    return this;
  };

  public isIgnored = (tag: IMusimapTag): boolean => {
    switch (this.type) {
      case MUSIMAP_WIDGETS.SIMPLE_TAGS_SELECTOR:
      case MUSIMAP_WIDGETS.CHART_SELECTOR:
      case MUSIMAP_WIDGETS.TEXT_SELECTOR:
      case MUSIMAP_WIDGETS.SIMPLE_TEXT_SELECTOR:
        return asArray(tag.content).some(item => this.ignoredPreferences.has(item.label));
      case MUSIMAP_WIDGETS.MULTI_SOURCE_TAGS_SELECTOR:
      case MUSIMAP_WIDGETS.MULTI_LEVEL_TAGS_SELECTOR:
        return asArray(tag.content).some(item => this.ignoredPreferences.has(`${item.category}~${item.label}`));
      default:
        return true;
    }
  };

  public get defaultValue(): any {
    return this.default;
  }

  public get label(): string {
    return this.proto.label;
  }

  public get type(): string {
    return this.proto.type;
  }

  public get sources(): IMusimapFilterCore[] {
    return this.proto.sources;
  }

  public get values(): any {
    return this.proto.values;
  }

  public get operator(): string | null {
    return this.isConjunctive ? (this.isConjunctive.value ? MUSIMAP_OPERATORS.AND : MUSIMAP_OPERATORS.OR) : null;
  }

  public get useDefaultOperator(): boolean {
    return !!this.proto.useDefaultOperator;
  }

  public setOpenedLocation(location: string): void {
    this.openedLocation = location;
  }
}
